/** React binding for RoomClient: snapshot, connection status, errors, emotes, Tibbs requests. */
import { useEffect, useMemo, useRef, useState } from 'react';
import { RoomClient } from '../net/roomClient';
import type { ConnectionStatus, FatalReason, RoomSnapshot, TibbsCall } from '../net/roomClient';
import type { Emote, SeatView } from '../../worker/protocol';
import type { SeatInfo } from './types';

export interface EmoteEvent {
  id: number;
  seat: number;
  emote: Emote;
}

/** A relayed Tibbs request, numbered so the table plays each one once. */
export interface TibbsCallEvent extends TibbsCall {
  id: number;
}

export interface RoomHook {
  client: RoomClient | null;
  snapshot: RoomSnapshot | null;
  /** Local clock (ms) when the snapshot arrived; anchors the idle-turn countdowns. */
  receivedAt: number;
  status: ConnectionStatus;
  fatal: FatalReason | null;
  error: { id: number; message: string } | null;
  emotes: EmoteEvent[];
  /** Recent Tibbs requests (the last 10), oldest first. */
  tibbsCalls: TibbsCallEvent[];
  /** The game's shared seed for Tibbs's line choices; null in the lobby. */
  tibbsSeed: number | null;
}

export function useRoom(code: string | null, name: string | null): RoomHook {
  const [snapshot, setSnapshot] = useState<RoomSnapshot | null>(null);
  const [receivedAt, setReceivedAt] = useState(0);
  const [status, setStatus] = useState<ConnectionStatus>('idle');
  const [fatal, setFatal] = useState<FatalReason | null>(null);
  const [error, setError] = useState<RoomHook['error']>(null);
  const [emotes, setEmotes] = useState<EmoteEvent[]>([]);
  const [tibbsCalls, setTibbsCalls] = useState<TibbsCallEvent[]>([]);
  const seq = useRef(0);

  const client = useMemo(() => {
    if (!code || !name) return null;
    try {
      return new RoomClient({ code, name });
    } catch {
      return null;
    }
  }, [code, name]);

  useEffect(() => {
    if (!client) return;
    setSnapshot(null);
    setFatal(null);
    const offs = [
      client.subscribe((snap) => {
        setSnapshot(snap);
        setReceivedAt(Date.now());
      }),
      client.on('status', (s, f) => {
        setStatus(s);
        if (f && f !== 'closed-by-client') setFatal(f);
      }),
      client.on('error', (message) => setError({ id: ++seq.current, message })),
      client.on('emote', (seat, emote) => {
        const ev = { id: ++seq.current, seat, emote };
        setEmotes((list) => [...list.slice(-8), ev]);
      }),
      client.on('tibbs', (call) => {
        const ev = { ...call, id: ++seq.current };
        setTibbsCalls((list) => [...list.slice(-9), ev]);
      }),
    ];
    client.connect();
    return () => {
      offs.forEach((off) => off());
      client.close();
    };
  }, [client]);

  const tibbsSeed = snapshot?.room.tibbsSeed ?? null;
  return { client, snapshot, receivedAt, status, fatal, error, emotes, tibbsCalls, tibbsSeed };
}

export function seatInfoFromView(v: SeatView, receivedAt = Date.now()): SeatInfo {
  return {
    name: v.name,
    isBot: v.isBot,
    ...(v.botLevel ? { level: v.botLevel } : {}),
    connected: v.connected,
    autopilot: v.autopilot,
    ready: v.ready,
    turnDeadline: typeof v.turnMsLeft === 'number' ? receivedAt + v.turnMsLeft : null,
    ...(v.stepIn ? { stepIn: true } : {}),
  };
}
